import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import TaskEditorForm from '../components/TaskEditorForm';
import { taskNavRef } from '../utils/taskNav';
import { useAuth } from '../context/AuthContext';

export default function TaskEditorPage() {
  const { user } = useAuth();
  const isCatalogAdmin = Boolean(user?.is_staff || user?.is_admin);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [lastCreatedId, setLastCreatedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    tag: '',
    no_solution: false,
    no_hints: false,
    no_tags: false,
  });
  const [similarTasks, setSimilarTasks] = useState([]);
  const [similarLoadError, setSimilarLoadError] = useState('');
  const [similarSlugInput, setSimilarSlugInput] = useState('');
  const [similarBusy, setSimilarBusy] = useState(false);
  const [similarActionError, setSimilarActionError] = useState('');

  const selectedTaskRef = selectedTask?.id ? taskNavRef(selectedTask) : null;

  useEffect(() => {
    if (!isCatalogAdmin || !selectedTaskRef) {
      setSimilarTasks([]);
      setSimilarLoadError('');
      setSimilarActionError('');
      return;
    }
    let cancelled = false;
    setSimilarLoadError('');
    setSimilarActionError('');
    api
      .get(`/tasks/${selectedTaskRef}/similar/`)
      .then((res) => {
        if (!cancelled) setSimilarTasks(Array.isArray(res.data) ? res.data : res.data?.results ?? []);
      })
      .catch(() => {
        if (!cancelled) {
          setSimilarTasks([]);
          setSimilarLoadError('Не удалось загрузить список аналогов.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isCatalogAdmin, selectedTaskRef]);

  async function refreshSimilarList() {
    if (!selectedTaskRef) return;
    try {
      const res = await api.get(`/tasks/${selectedTaskRef}/similar/`);
      setSimilarTasks(Array.isArray(res.data) ? res.data : res.data?.results ?? []);
    } catch {
      setSimilarLoadError('Не удалось обновить список аналогов.');
    }
  }

  async function addSimilarTask() {
    const slug = similarSlugInput.trim();
    if (!slug || !selectedTaskRef) return;
    setSimilarBusy(true);
    setSimilarActionError('');
    try {
      await api.post(`/tasks/${selectedTaskRef}/similar/`, { similar_slug: slug });
      setSimilarSlugInput('');
      await refreshSimilarList();
    } catch (e) {
      const d = e.response?.data;
      setSimilarActionError(typeof d?.detail === 'string' ? d.detail : 'Не удалось добавить аналог.');
    } finally {
      setSimilarBusy(false);
    }
  }

  async function unlinkSimilarTask(other) {
    if (!selectedTaskRef) return;
    setSimilarBusy(true);
    setSimilarActionError('');
    try {
      await api.delete(`/tasks/${selectedTaskRef}/similar/`, {
        data: { similar_slug: taskNavRef(other) },
      });
      await refreshSimilarList();
    } catch (e) {
      const d = e.response?.data;
      setSimilarActionError(typeof d?.detail === 'string' ? d.detail : 'Не удалось снять связь.');
    } finally {
      setSimilarBusy(false);
    }
  }

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filters.search) params.set('search', filters.search);
      if (filters.status) params.set('status', filters.status);
      if (filters.tag) params.set('tag', filters.tag);
      if (filters.no_solution) params.set('no_solution', '1');
      if (filters.no_hints) params.set('no_hints', '1');
      if (filters.no_tags) params.set('no_tags', '1');
      const res = await api.get(`/tasks/editor/?${params}`);
      setTasks(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch (e) {
      setError(e.response?.status === 403 ? 'Нет прав для редактирования задач' : 'Не удалось загрузить список');
    } finally {
      setLoading(false);
    }
  }, [filters.search, filters.status, filters.tag, filters.no_solution, filters.no_hints, filters.no_tags]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const loadTask = useCallback(async (slug) => {
    if (!slug) {
      setSelectedTask(null);
      return;
    }
    setError('');
    try {
      const res = await api.get(`/tasks/editor/${slug}/`);
      setSelectedTask(res.data);
    } catch (e) {
      setError(e.response?.status === 404 ? 'Задача не найдена' : 'Не удалось загрузить задачу');
      setSelectedTask(null);
    }
  }, []);

  const handleSave = async (payload, existingSlug) => {
    setSaving(true);
    setError('');
    try {
      if (existingSlug) {
        await api.patch(`/tasks/editor/${existingSlug}/`, payload);
        await loadTask(existingSlug);
        await fetchTasks();
      } else {
        const res = await api.post('/tasks/editor/', payload);
        setLastCreatedId(res.data.id);
        setSelectedTask(res.data);
        await fetchTasks();
      }
    } catch (e) {
      const msg = e.response?.data;
      if (typeof msg === 'object' && msg !== null) {
        const first = Object.entries(msg)[0];
        setError(first ? `${first[0]}: ${Array.isArray(first[1]) ? first[1][0] : first[1]}` : 'Ошибка сохранения');
      } else {
        setError('Ошибка сохранения');
      }
    } finally {
      setSaving(false);
    }
  };

  const startNew = () => {
    setSelectedTask({});
    setLastCreatedId(null);
    setError('');
  };

  return (
    <div className="task-editor-page">
      <div className="editor-layout">
        <aside className="editor-sidebar">
          <div className="editor-sidebar-header">
            <h2>Задачи</h2>
            <Link to="/teacher/tasks/import" className="btn btn-outline btn-sm">
              Пакетный импорт
            </Link>
          </div>

          <div className="editor-filters">
            <input
              type="text"
              placeholder="Поиск по названию и условию"
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              className="form-input"
            />
            <select
              value={filters.status}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
              className="form-select"
            >
              <option value="">Все статусы</option>
              <option value="draft">Черновик</option>
              <option value="published">Опубликовано</option>
              <option value="archived">Архив</option>
            </select>
            <label className="editor-filter-check">
              <input
                type="checkbox"
                checked={filters.no_solution}
                onChange={(e) => setFilters((f) => ({ ...f, no_solution: e.target.checked }))}
              />
              Без решения
            </label>
            <label className="editor-filter-check">
              <input
                type="checkbox"
                checked={filters.no_hints}
                onChange={(e) => setFilters((f) => ({ ...f, no_hints: e.target.checked }))}
              />
              Без подсказок
            </label>
            <label className="editor-filter-check">
              <input
                type="checkbox"
                checked={filters.no_tags}
                onChange={(e) => setFilters((f) => ({ ...f, no_tags: e.target.checked }))}
              />
              Без тегов
            </label>
          </div>

          {loading ? (
            <p className="text-secondary">Загрузка…</p>
          ) : (
            <ul className="editor-task-list">
              <li>
                <button type="button" className="editor-task-item new" onClick={startNew}>
                  + Новая задача
                </button>
              </li>
              {tasks.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    className={`editor-task-item ${selectedTask?.id === t.id ? 'active' : ''} ${lastCreatedId === t.id ? 'last-created' : ''}`}
                    onClick={() => loadTask(taskNavRef(t))}
                  >
                    <span className="editor-task-title">{t.title || taskNavRef(t) || `#${t.id}`}</span>
                    <span className="editor-task-meta">{t.status} · {t.answer_type}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <main className="editor-main">
          {selectedTask !== undefined && selectedTask !== null && Object.keys(selectedTask).length === 0 ? (
            <div className="editor-form-wrap">
              <h2>Новая задача</h2>
              <TaskEditorForm
                task={null}
                onSave={handleSave}
                onSaveAndNext={() => {}}
                saving={saving}
                error={error}
              />
            </div>
          ) : selectedTask && selectedTask.id ? (
            <div className="editor-form-wrap">
              <h2>Редактирование: {selectedTask.title || taskNavRef(selectedTask)}</h2>
              <TaskEditorForm
                task={selectedTask}
                onSave={handleSave}
                saving={saving}
                error={error}
              />
              {isCatalogAdmin ? (
                <section className="editor-similar-block">
                  <h3 className="editor-similar-heading">Аналоги (только администратор)</h3>
                  {similarLoadError ? <p className="text-secondary">{similarLoadError}</p> : null}
                  {similarActionError ? <div className="alert alert-error">{similarActionError}</div> : null}
                  {similarTasks.length === 0 && !similarLoadError ? (
                    <p className="text-secondary">Пока нет связанных аналогов.</p>
                  ) : (
                    <ul className="editor-similar-list">
                      {similarTasks.map((st) => (
                        <li key={st.id} className="editor-similar-row">
                          <Link to={`/work/${taskNavRef(st)}`} className="editor-similar-link">
                            {st.title || taskNavRef(st)}
                          </Link>
                          <button
                            type="button"
                            className="btn btn-outline btn-xs"
                            disabled={similarBusy}
                            onClick={() => void unlinkSimilarTask(st)}
                          >
                            Отвязать
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="editor-similar-add">
                    <label className="form-group" style={{ marginBottom: 0 }}>
                      <span className="text-secondary" style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.85rem' }}>
                        Slug или id другой задачи
                      </span>
                      <input
                        type="text"
                        className="form-input"
                        value={similarSlugInput}
                        onChange={(e) => setSimilarSlugInput(e.target.value)}
                        placeholder="например other-task-slug или 42"
                        disabled={similarBusy}
                      />
                    </label>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={similarBusy || !similarSlugInput.trim()}
                      onClick={() => void addSimilarTask()}
                    >
                      Добавить аналог
                    </button>
                  </div>
                </section>
              ) : null}
            </div>
          ) : (
            <div className="editor-placeholder">
              <p>Выберите задачу из списка или нажмите «Новая задача».</p>
              <button type="button" className="btn btn-primary" onClick={startNew}>
                Создать задачу
              </button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
